import type { ChatMessage, OrderCardData, RequestScrollCallback } from '@chewy/chat-library-common';
import { PaginatedSelector } from '@chewy/chat-library-components';
import OrderCard from './OrderCard';
const MAX_CARDS_PER_PAGE = 3;

interface OrderCardSelectorProps {
  /**
   * Array of orders to display
   */
  data: Array<OrderCardData>;
  /**
   * Layout direction for the cards
   * @default 'vertical'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Chat message associated with the orders
   */
  message?: ChatMessage;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Page offset for the cards
   * @default 3
   */
  pageOffset?: number;
  /**
   * Callback function to handle selection
   */
  onSelect?: (item: OrderCardData) => void;
  /**
   * Callback function to request scrolling to a selected item
   */
  requestScroll?: RequestScrollCallback;
}

/**
 * Displays a list of selectable OrderCards with pagination
 * Uses PaginatedSelector to handle pagination and action buttons
 */
export default function OrderCardSelector({
  data,
  direction = 'vertical',
  message,
  className,
  pageOffset = MAX_CARDS_PER_PAGE,
  onSelect,
  requestScroll
}: OrderCardSelectorProps) {
  const itemType = data?.length > 0 ? data[0]?.cardType === 'AUTOSHIP_LIST_COMPONENT' ? 'autoship' : 'order' : 'order';
  return (
    <PaginatedSelector
      data={data}
      renderItem={(order, index, { ref, handleSelect }) => {
        return (
          <OrderCard
            key={order.orderId ?? index}
            ref={ref}
            data={[order]}
            onSelect={() => handleSelect(message?.responseTimestamp)}
            isSelectable={true}
          />
        );
      }}
      direction={direction}
      className={className}
      pageSize={pageOffset}
      itemType={itemType}
      onSelect={onSelect}
      requestScroll={requestScroll}
      showMoreAction={[{
        defaultAction: {
          type: 'button',
          text: 'Show more',
          payload: {
            action: 'SHOW_MORE_OPTIONS',
          },
        },
      }]}
      secondaryAction={[{
        defaultAction: {
          type: 'button',
          text: itemType === 'autoship'
            ? 'Need help with different Autoship'
            : 'Need help with a different order',
          payload: {
            action: 'SHOW_DIFFERENT_ORDER_OPTIONS',
          },
        },
      }]}
    />
  );
}
